import type { Diamond, PeerBand } from '../api/types'

/**
 * lib/peerBands.ts — turning a band into a POSITION.
 *
 * ── THE DIVISION OF LABOUR ────────────────────────────────────────────────
 * The bucketing rules and the quantiles live on the server
 * (`functions/src/shared/peerBands.ts`) and arrive as
 * `SearchResponse.peerBands` keyed by `stone.specs.peerKey`. Nothing in this
 * file re-derives a bucket, because two implementations of "which stones are
 * comparable" is precisely the drift that would have the desktop card and the
 * mobile card quoting different markets for the same stone.
 *
 * What this file owns is the last hop: given a stone's $/ct and its band,
 * where does the tick sit, and what is the sentence.
 *
 * ── WHY EVERY GATE BELOW IS A `null` AND NOT A DASH ───────────────────────
 * A wrong position is worse than no position — it is the one thing on the
 * browse surface a trader would actually act on. So every failure mode
 * (no band, locked price, degenerate distribution) returns null and the mark
 * renders nothing at all. Not a dash, not a skeleton, not "insufficient data":
 * the surface's existing contract is that it never prints an em-dash for
 * something it does not hold, and this holds that line.
 */

/**
 * The smallest bucket we will position against. Mirrors MIN_PEER_GROUP on the
 * server, which already omits smaller buckets from the map — this is the
 * belt-and-braces half, so a hand-crafted or stale payload cannot talk the UI
 * into drawing a distribution over five stones.
 */
export const MIN_PEER_N = 9

/** Below this, the difference from the median is noise, not a position. */
const AT_MEDIAN_PCT = 3

/**
 * Where p25 and p75 sit on the track, as fractions of its width.
 *
 * The axis is the interquartile range, not the full spread: the middle half of
 * the market occupies the middle half of the bar, and the tails get the
 * remaining quarter each. Anchoring on the IQR rather than min/max is what
 * stops one absurdly-priced stone from squashing every other tick into a
 * pixel. MUST stay in sync with the warm band painted in `stonePeer.css`.
 */
const P25_POS = 0.26
const P75_POS = 0.74
/** Ticks never reach the very edge — a tick flush with the rim reads as clipped. */
const POS_MIN = 0.04
const POS_MAX = 0.96

export interface PeerPosition {
  /** Stones in the bucket, including this one. */
  n: number
  p25: number
  p50: number
  p75: number
  /** Signed % difference from the bucket's median $/ct. */
  deltaPct: number
  /** 0..1 position on the track. 0.5 is the median. */
  pos: number
  read: 'under' | 'at' | 'over'
  /** "Round, G–J, VS1–VS2, 1.00–1.49ct" */
  label: string
}

/**
 * Piecewise-linear map from a $/ct onto the track. Linear within the IQR,
 * linear (and compressed) outside it, clamped so a tick is always on the bar.
 */
function positionFor(rate: number, band: PeerBand): number {
  const { p25, p50, p75 } = band
  let pos: number
  if (rate <= p50) {
    // Below the median: p25 → P25_POS, p50 → 0.5. A degenerate lower half
    // (p25 === p50, i.e. a quarter of the market at one price) would divide by
    // zero, so it collapses to the median position rather than to Infinity.
    const span = p50 - p25
    pos = span > 0 ? 0.5 - ((p50 - rate) / span) * (0.5 - P25_POS) : 0.5
  } else {
    const span = p75 - p50
    pos = span > 0 ? 0.5 + ((rate - p50) / span) * (P75_POS - 0.5) : 0.5
  }
  return Math.min(POS_MAX, Math.max(POS_MIN, pos))
}

/**
 * The stone's position in its market, or null when we cannot state one
 * honestly.
 *
 * Null when: no bands arrived (unverified caller — the server withheld them),
 * the stone has no bucket, its bucket is not in the map, the bucket is under
 * quorum, the price is locked, or the median is not a positive finite number.
 *
 * `mult` folds in the Aska Services fee when that mode is on, so the mark
 * compares like with like: with the fee showing, the buyer is reading a
 * fee-inclusive price, and the whole peer set is lifted by the same factor —
 * which is why the delta is unchanged and the position is too. Passing it
 * through anyway keeps the arithmetic honest if the fee ever stops being flat.
 */
export function peerFor(
  stone: Diamond,
  bands: Record<string, PeerBand> | undefined,
  mult = 1,
): PeerPosition | null {
  if (!bands) return null
  const key = stone.specs?.peerKey
  if (!key) return null
  const band = bands[key]
  if (!band) return null
  if (!(band.n >= MIN_PEER_N)) return null

  const rate = stone.pricePerCarat
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) return null

  const p50 = band.p50 * mult
  const p25 = band.p25 * mult
  const p75 = band.p75 * mult
  if (!Number.isFinite(p50) || p50 <= 0) return null

  const shown = rate * mult
  const deltaPct = ((shown - p50) / p50) * 100
  const read: PeerPosition['read'] =
    Math.abs(deltaPct) < AT_MEDIAN_PCT ? 'at' : deltaPct < 0 ? 'under' : 'over'

  return {
    n: band.n,
    p25, p50, p75,
    deltaPct,
    pos: positionFor(shown, { ...band, p25, p50, p75 }),
    read,
    label: band.label,
  }
}

/**
 * The one-line read.
 *
 * Every word here is deliberate and none of them may drift:
 *
 *   · "comparable" — never "market". We are stating a fact about OUR catalog,
 *     which is the only thing we can defend. "Below market" is a valuation
 *     claim about the global diamond trade that we cannot support and that
 *     counsel has not cleared (#29).
 *   · "on Diaex" — names the denominator, out loud, in the UI.
 *   · never "deal", "bargain", "great value" or any competitor's number.
 */
export function peerSentence(p: PeerPosition): string {
  const pct = Math.round(Math.abs(p.deltaPct))
  if (p.read === 'at') return `in line with ${p.n} comparable`
  return `${pct}% ${p.read} ${p.n} comparable`
}

/** The same read as a full sentence for a screen reader — no abbreviations. */
export function peerSpoken(p: PeerPosition): string {
  const pct = Math.round(Math.abs(p.deltaPct))
  const head = p.read === 'at'
    ? `In line with the median of ${p.n} comparable stones on Diaex`
    : `${pct} percent ${p.read} the median of ${p.n} comparable stones on Diaex`
  return `${head} — ${p.label}.`
}

/** The expanded explainer: what the group is, and what its middle costs. */
export function peerExplainer(p: PeerPosition): string {
  const median = `$${Math.round(p.p50).toLocaleString()}/ct`
  return `Compared with ${p.n} stones on Diaex: ${p.label}. Median ${median}.`
}
